/**
 * SURFACE GROUPING for the elevation-panel-v2 surface ribbon.
 *
 * `surfaces.ts` defines 19 raw OSM-style surface codes (paved, asphalt,
 * cobblestone, gravel, dirt, ice, …) — accurate, but far too many distinct
 * colours to read at a glance in a 14px-tall ribbon + legend. This module
 * adds a grouping layer on top, mapping every one of the 19 codes onto one of
 * 6 visually-distinct, ribbon-friendly groups. `surfaces.ts` itself (and its
 * consumer, the original `ElevationProfile.vue`) is untouched.
 *
 * The grouping is a UX judgment call, not derived from data — see the
 * elevation-panel-v2 plan's Open Questions (`wood`/`metal`/`unpaved` in
 * particular are debatable placements). Groups are distinguishable by both
 * hue and value, not hue alone, for colourblind users.
 *
 * Planned routes only: recordings never have surface data (see `surfaces.ts`
 * / the elevation-panel-v2 plan for why), so this module is only ever
 * exercised in `mode="planned"`.
 */

import { surfaceIntToKey } from './surfaces';

export type SurfaceGroupKey = 'paved' | 'hardpacked' | 'naturalSoft' | 'loose' | 'iceSnow' | 'unknown';

export interface SurfaceGroup {
  key: SurfaceGroupKey;
  /** Short human label for a legend, e.g. `'Hardpacked'`. */
  label: string;
  /** Representative swatch colour for the legend/ribbon. */
  color: string;
}

/** Fallback colour when a surface code doesn't resolve to a known group. */
export const DEFAULT_SURFACE_GROUP_COLOR = '#BDBDBD';

// Brighter/more saturated than the original set — these are drawn as a thin
// line over the (also colourful) steepness fill on a dark chart background,
// so muted tones like olive drab or tan read as dull/washed-out at that
// weight. Kept one hue family per group for the same "is this paved/loose/
// natural" read, just punched up in saturation + lightness.
export const SURFACE_GROUPS: Record<SurfaceGroupKey, SurfaceGroup> = {
  paved: { key: 'paved', label: 'Paved', color: '#B0BEC5' },
  hardpacked: { key: 'hardpacked', label: 'Hardpacked', color: '#FFA726' },
  naturalSoft: { key: 'naturalSoft', label: 'Natural / soft', color: '#66BB6A' },
  loose: { key: 'loose', label: 'Loose', color: '#FFEE58' },
  iceSnow: { key: 'iceSnow', label: 'Ice / snow', color: '#4FC3F7' },
  unknown: { key: 'unknown', label: 'Unknown', color: DEFAULT_SURFACE_GROUP_COLOR },
};

/**
 * Maps every `surfaceIntToKey` value (see `surfaces.ts`) onto one of the 6
 * groups above. Every one of the 19 known surface keys appears exactly once.
 */
export const SURFACE_KEY_TO_GROUP: Record<string, SurfaceGroupKey> = {
  // Paved — smooth, engineered, vehicle-grade surfaces.
  paved: 'paved',
  asphalt: 'paved',
  concrete: 'paved',
  pavingStones: 'paved',
  cobblestone: 'paved',

  // Hardpacked — firm underfoot but not smooth; includes engineered
  // non-paved surfaces (boardwalk planking, metal decking/grates) and OSM's
  // generic "unpaved" catch-all, which has no more specific tag.
  compactedGravel: 'hardpacked',
  fineGravel: 'hardpacked',
  gravel: 'hardpacked',
  unpaved: 'hardpacked',
  wood: 'hardpacked',
  metal: 'hardpacked',

  // Natural / soft — organic ground cover.
  dirt: 'naturalSoft',
  ground: 'naturalSoft',
  grass: 'naturalSoft',
  grassPaver: 'naturalSoft',
  woodchips: 'naturalSoft',

  // Loose — underfoot conditions that shift/give way.
  sand: 'loose',

  // Ice / snow.
  ice: 'iceSnow',

  // Unknown.
  unknown: 'unknown',
};

/**
 * Resolve a raw surface integer (as delivered on the wire, see `surfaces.ts`)
 * straight to its group. Unknown/undefined/unrecognised values resolve to
 * the `unknown` group.
 */
export function surfaceGroupForValue(value: number | null | undefined): SurfaceGroup {
  const key = value != null ? surfaceIntToKey[value] : undefined;
  const groupKey = (key && SURFACE_KEY_TO_GROUP[key]) || 'unknown';
  return SURFACE_GROUPS[groupKey];
}

/**
 * Convenience: map a full `surfaces[]` array (e.g. `ElevationChartData.surfaces`
 * from `buildElevationChartData.ts`) to group keys, one per sample point.
 */
export function computeSurfaceGroups(surfaces: (number | undefined)[]): SurfaceGroupKey[] {
  return surfaces.map((s) => surfaceGroupForValue(s).key);
}
